/**
 * EventHistoryStrip — form ticks (1a) + result rail (1f).
 *
 * Two modes:
 *   - `binary` (default): yes/no per match. Use for BTTS hit, Over 2,5 cover,
 *     clean sheet, scored first — anything that's "did the thing happen?".
 *     Each outcome is `{ hit: boolean; label? }`. Renders as the RESULT RAIL:
 *     hit = tall tile with a status bar on top; miss = short quiet tile.
 *   - `three-state`: W/D/L form letters. Use for form-strips on 1X2 markets.
 *     Each outcome is `{ state: 'win' | 'draw' | 'loss'; label? }`. Renders as
 *     FORM TICKS: height encodes the result (win 18 / draw 10 / loss 5) and
 *     colour confirms it, so the strip stays readable without colour.
 *     We deliberately don't collapse Draw into the binary axis — that's
 *     information loss that the LLM was being forced into in Phase 5C.
 *
 * Claude Design v4 signature: both modes seat their marks on a 2px rail,
 * butt caps, no round terminals. The newest mark is wider and at full
 * opacity; older marks fade back (`--formtick-recency-fade`). The caption
 * sits left in caps, the summary count right in the display face.
 *
 * The letters (V/O/F, Ja/Nej) remain in the DOM for assistive tech and for
 * the binary tiles; in three-state they are visually hidden because the
 * tick height already carries the meaning.
 *
 * Colour source: STATUS (`--form-*`, `--status-*`). The ONE brand element is
 * the optional NEXT marker. See components/widget-color-sources.md.
 */

import type { CSSProperties, ReactElement } from 'react';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import type { PrimitiveMeta } from './_meta.js';
import styles from './EventHistoryStrip.module.css';

// Outcome union — binary OR three-state. Same array, two possible item
// shapes. Discriminated by the presence of `hit` vs `state` at runtime.
const BinaryOutcomeSchema = z.object({
  hit: z.boolean(),
  /** Optional per-chip tooltip (e.g. "vs Hammarby 2-1"). */
  label: z.string().optional(),
});

const ThreeStateOutcomeSchema = z.object({
  state: z.enum(['win', 'draw', 'loss']),
  /** Optional per-chip tooltip. */
  label: z.string().optional(),
});

const dataShape = z.object({
  /** Oldest → newest. Min 1, max 20. Binary OR three-state. */
  outcomes: z.array(z.union([BinaryOutcomeSchema, ThreeStateOutcomeSchema])).min(1).max(20),
  /** Mode hint. Default 'binary'. Drives default labels + colour tokens. */
  mode: z.enum(['binary', 'three-state']).optional(),
  /** Small label rendered above the mark row. Optional but recommended —
   *  without it the marks are visually ambiguous. */
  caption: z.string().optional(),
  // Binary-mode labels. When omitted, resolved from the i18n catalog
  // (chip.yes / chip.no) — never a hardcoded language.
  /** Displayed text inside a hit tile. Default = t('chip.yes'). */
  hitLabel: z.string().optional(),
  /** Displayed text inside a miss tile. Default = t('chip.no'). */
  missLabel: z.string().optional(),
  /** Solid colour of hit tiles. Default --status-positive. */
  hitColor: z.string().optional(),
  /** Text colour of miss tiles. Default --status-neutral. */
  missColor: z.string().optional(),
  // Three-state labels
  winLabel: z.string().optional(),
  drawLabel: z.string().optional(),
  lossLabel: z.string().optional(),
  size: z.enum(['sm', 'md']).optional(),
  /** Renders the accent NEXT diamond after the newest mark. */
  showNextMarker: z.boolean().optional(),
  ariaLabel: z.string(),
});

export const META: PrimitiveMeta = {
  name: 'event-history-strip',
  description:
    'Rail of marks for the last N matches. Binary result rail (BTTS hit, Over 2,5 cover) or three-state form ticks (W/D/L).',
  composable: true,
  dataShape,
  densities: ['compact', 'standard'],
  breakpoints: { mobile: true, wide: true },
  animation: 'fade-in',
};

export type EventHistoryBinaryOutcome = {
  hit: boolean;
  label?: string;
};

export type EventHistoryThreeStateOutcome = {
  state: 'win' | 'draw' | 'loss';
  label?: string;
};

export type EventHistoryOutcome =
  | EventHistoryBinaryOutcome
  | EventHistoryThreeStateOutcome;

export interface EventHistoryStripProps {
  outcomes: EventHistoryOutcome[];
  mode?: 'binary' | 'three-state';
  caption?: string;
  // Binary-mode labels
  hitLabel?: string;
  missLabel?: string;
  hitColor?: string;
  missColor?: string;
  // Three-state labels
  winLabel?: string;
  drawLabel?: string;
  lossLabel?: string;
  size?: 'sm' | 'md';
  /** Renders the accent NEXT diamond after the newest mark. */
  showNextMarker?: boolean;
  ariaLabel: string;
}

function isThreeState(o: EventHistoryOutcome): o is EventHistoryThreeStateOutcome {
  return (o as EventHistoryThreeStateOutcome).state !== undefined;
}

/**
 * Recency fade — the oldest mark sits at `--formtick-recency-fade`, the
 * newest at full strength, linearly interpolated. Single-item strips stay
 * at 1.0 (there is no "older" to contrast against).
 *
 * Expressed as a CSS `calc()` over the token rather than a JS constant, so
 * a brand that overrides `--formtick-recency-fade` actually changes the
 * fade. Returns a CSS value, not a number.
 */
function recencyOpacity(index: number, count: number): string {
  if (count <= 1) return '1';
  const t = index / (count - 1);
  return `calc(var(--formtick-recency-fade, 0.55) + (1 - var(--formtick-recency-fade, 0.55)) * ${t})`;
}

export function EventHistoryStrip({
  outcomes,
  mode,
  caption,
  hitLabel,
  missLabel,
  hitColor,
  missColor,
  winLabel,
  drawLabel,
  lossLabel,
  size = 'sm',
  showNextMarker = false,
  ariaLabel,
}: EventHistoryStripProps): ReactElement {
  const { t } = useTranslation();
  // Labels resolve from the i18n catalog when not explicitly supplied —
  // chip Yes/No and W/D/L form letters are localized, never hardcoded.
  const hitText = hitLabel ?? t('chip.yes');
  const missText = missLabel ?? t('chip.no');
  const winText = winLabel ?? t('form.W');
  const drawText = drawLabel ?? t('form.D');
  const lossText = lossLabel ?? t('form.L');
  // Mode auto-detected from outcome shape if not supplied — defends
  // against an operator who set `mode='three-state'` but passed binary
  // outcomes (or vice versa). Explicit prop wins.
  const detectedMode: 'binary' | 'three-state' = (() => {
    if (mode) return mode;
    const first = outcomes[0];
    return first && isThreeState(first) ? 'three-state' : 'binary';
  })();

  const hit = hitColor ?? 'var(--status-positive, #197a3b)';
  const miss = missColor ?? 'var(--status-neutral, #6e6e75)';
  const winColor = 'var(--form-win, #197a3b)';
  const drawColor = 'var(--form-draw, #a26500)';
  const lossColor = 'var(--form-loss, #c12121)';

  const summary = (() => {
    if (detectedMode === 'three-state') {
      let w = 0;
      let d = 0;
      let l = 0;
      for (const o of outcomes) {
        if (isThreeState(o)) {
          if (o.state === 'win') w += 1;
          else if (o.state === 'draw') d += 1;
          else l += 1;
        }
      }
      return `${w}${winText} ${d}${drawText} ${l}${lossText}`;
    }
    const hits = outcomes.filter((o) => !isThreeState(o) && o.hit).length;
    return `${hits} of ${outcomes.length} hit`;
  })();

  /** Right-aligned compact count next to the caption. */
  const summaryCount = (() => {
    if (detectedMode === 'three-state') return summary;
    const hits = outcomes.filter((o) => !isThreeState(o) && o.hit).length;
    return `${hits}/${outcomes.length}`;
  })();

  const count = outcomes.length;

  return (
    <div className={styles.wrap}>
      {caption && caption.length > 0 ? (
        <div className={styles.captionRow}>
          <div className={styles.caption}>{caption}</div>
          <div className={styles.summaryCount} aria-hidden="true">
            {summaryCount}
          </div>
        </div>
      ) : null}
      <div
        className={styles.strip}
        role="img"
        aria-label={`${ariaLabel}. ${summary}.`}
        data-size={size}
      >
        {outcomes.map((o, i) => {
          const isLatest = i === count - 1;
          const opacity = isLatest ? 1 : recencyOpacity(i, count);
          const animStyle = {
            '--atlas-ehs-d': `${Math.min(i * 40, 240)}ms`,
          } as CSSProperties & Record<'--atlas-ehs-d', string>;

          let style: CSSProperties;
          let text: string;
          let markClass: string;
          let stateAttr: string;

          if (detectedMode === 'three-state' && isThreeState(o)) {
            const colour =
              o.state === 'win' ? winColor : o.state === 'loss' ? lossColor : drawColor;
            text = o.state === 'win' ? winText : o.state === 'loss' ? lossText : drawText;
            stateAttr = o.state;
            style = { background: colour, opacity };
            markClass = `${styles.tick ?? ''} ${isLatest ? (styles.tickLatest ?? '') : ''}`;
          } else if (!isThreeState(o)) {
            const bin = o;
            text = bin.hit ? hitText : missText;
            stateAttr = bin.hit ? 'hit' : 'miss';
            style = bin.hit
              ? {
                  borderTopColor: hit,
                  // 8% tint of the status colour, resolved by the browser.
                  background: `color-mix(in srgb, ${hit} 8%, transparent)`,
                  color: hit,
                  opacity,
                }
              : { color: miss, opacity };
            markClass = bin.hit ? (styles.tileHit ?? '') : (styles.tileMiss ?? '');
            markClass = `${styles.tile ?? ''} ${markClass}`;
          } else {
            // Defensive: mode='binary' but outcome is three-state — render a
            // neutral tick rather than crashing.
            stateAttr = 'draw';
            text = '?';
            style = { background: drawColor, opacity };
            markClass = styles.tick ?? '';
          }

          const className = `${styles.mark} ${markClass}`;
          const isTick = detectedMode === 'three-state';

          return (
            <span
              key={i}
              className={className}
              data-state={stateAttr}
              style={{ ...style, ...animStyle }}
              aria-hidden="true"
              {...(o.label !== undefined && o.label.length > 0 ? { title: o.label } : {})}
            >
              {isTick ? <span className={styles.srOnly}>{text}</span> : <span>{text}</span>}
            </span>
          );
        })}
        {showNextMarker ? (
          <span className={styles.nextMarker} aria-hidden="true" />
        ) : null}
        <span className={styles.srOnly}>{summary}</span>
      </div>
    </div>
  );
}

export default EventHistoryStrip;
